import { normalizedToPixel } from '@peruselab/structure-editor/domain/bonds';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import type { RenderableAnnotation } from '@peruselab/structure-editor/domain/annotationRendering';
import {
  estimateTextWidth,
  layoutText,
  plainText,
  type TextMeasure
} from '@peruselab/structure-editor/rendering/textLayout';

let measureContext: CanvasRenderingContext2D | null | undefined;

// Real advance widths from a shared offscreen canvas; falls back to the layout estimate where
// canvas text metrics are unavailable (jsdom).
export const canvasTextMeasure: TextMeasure = (text, font) => {
  if (measureContext === undefined) {
    try {
      measureContext = document.createElement('canvas').getContext('2d');
    } catch {
      measureContext = null;
    }
  }
  if (!measureContext) return estimateTextWidth(text, font);
  measureContext.font = `${font.italic ? 'italic ' : ''}${font.bold ? '700 ' : '400 '}${font.fontSizePx}px ${font.fontFamily}`;
  return measureContext.measureText(text).width;
};

// #756 -- one standalone rich-text annotation on the paper. Paper user units are px at 100% zoom
// (the parent viewBox scales with zoom), so sizes lay out at scale 1. Run text is rendered as React
// children, which escapes it: markup-looking text stays literal.
export function PaperTextAnnotation({
  annotation,
  paperSize,
  selected = false
}: {
  readonly annotation: Extract<RenderableAnnotation, { kind: 'text' }>;
  readonly paperSize: PaperSize;
  readonly selected?: boolean;
}) {
  const origin = normalizedToPixel(annotation.position, paperSize);
  const layout = layoutText(annotation.paragraphs, origin, canvasTextMeasure);
  return (
    <g
      className="text-annotation"
      data-annotation-id={annotation.id}
      data-selected={selected || undefined}
      fill={annotation.color ?? 'var(--text)'}
      stroke="none"
      opacity={annotation.opacity}
      role="img"
      aria-label={plainText(annotation.paragraphs)}
    >
      {/* Hit target for the text and select tools (the vector layer itself ignores pointers). */}
      <rect
        className={`text-annotation-hit${selected ? ' selected' : ''}`}
        x={origin.x}
        y={origin.y}
        width={layout.width}
        height={layout.height}
        fill="transparent"
      />
      {layout.lines.map((line, lineIndex) =>
        line.spans.length === 0 ? null : (
          <text key={lineIndex} x={line.x} y={line.baseline} textAnchor={line.anchor} xmlSpace="preserve">
            {line.spans.map((span, spanIndex) => (
              <tspan
                key={spanIndex}
                dy={span.dy || undefined}
                fontFamily={span.fontFamily}
                fontSize={span.fontSizePx}
                fontWeight={span.bold ? 700 : 400}
                fontStyle={span.italic ? 'italic' : undefined}
              >
                {span.text}
              </tspan>
            ))}
          </text>
        )
      )}
    </g>
  );
}
